import { DragDropContext, Draggable, Droppable, OnDragEndResponder } from "@hello-pangea/dnd";
import { createPortal } from "react-dom";
import { BorderBox } from "@/components/atoms/BorderBox";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { useFieldContext } from "@/features/question/components/organisms/FieldRenderer";

// Updated type definition for two-dimensional structure
interface PickGroupRankOptions {
    groups: string[];
    options: string[]; // All available options
    groupedOptions?: { [groupName: string]: string[] }; // Options assigned to groups
}

export function PickGroupRankRenderer() {
    const { question, disabled, response, form } = useFieldContext();

    const [groupsAndOptions, setGroupsAndOptions] = useState<PickGroupRankOptions>(() => {
        const parsed = question?.field_options || { groups: [], options: [], groupedOptions: {} };

        // Initialize grouped options for each group if not present
        const initialGroupedOptions: { [key: string]: string[] } = {};
        parsed.groups?.forEach(group => {
            initialGroupedOptions[group] = parsed.groupedOptions?.[group] || [];
        });

        return {
            groups: parsed.groups || [],
            options: parsed.options || [],
            groupedOptions: initialGroupedOptions
        };
    });

    useEffect(() => {
        if (!form) return;
        form.setValue('additional', JSON.stringify(groupsAndOptions));
    }, [groupsAndOptions, form]);

    const onDragEnd: OnDragEndResponder = (result) => {
        const { destination, source } = result;

        // Do nothing if dropped outside any droppable area
        if (!destination) {
            return;
        }

        // Do nothing if dropped in the same position
        if (destination.droppableId === source.droppableId && destination.index === source.index) {
            return;
        }

        const sourceGroupName = source.droppableId;
        const destinationGroupName = destination.droppableId;

        setGroupsAndOptions(prevState => {
            const newState = { ...prevState };

            // Get the item being moved
            let movedItem: string;

            if (sourceGroupName === 'ungrouped') {
                // Moving from ungrouped to a group or reordering within ungrouped
                const ungroupedItems = [...newState.options];
                [movedItem] = ungroupedItems.splice(source.index, 1);
                newState.options = ungroupedItems;
            } else {
                // Moving from a group
                const sourceGroup = [...(newState.groupedOptions?.[sourceGroupName] || [])];
                [movedItem] = sourceGroup.splice(source.index, 1);
                newState.groupedOptions = {
                    ...newState.groupedOptions,
                    [sourceGroupName]: sourceGroup
                };
            }

            if (destinationGroupName === 'ungrouped') {
                // Moving to ungrouped
                const ungroupedItems = [...newState.options];
                ungroupedItems.splice(destination.index, 0, movedItem);
                newState.options = ungroupedItems;
            } else {
                // Moving to a group
                const destinationGroup = [...(newState.groupedOptions?.[destinationGroupName] || [])];
                destinationGroup.splice(destination.index, 0, movedItem);
                newState.groupedOptions = {
                    ...newState.groupedOptions,
                    [destinationGroupName]: destinationGroup
                };
            }

            return newState;
        });
    };

    // Render disabled state
    if (disabled) {
        const responseData: PickGroupRankOptions = response?.field_value ? JSON.parse(response.field_value) : groupsAndOptions;
        const groupsWithElements = responseData.groups.filter(groupName => (responseData.groupedOptions?.[groupName]?.length || 0) > 0);
        return (
            <div className={cn(
                "grid w-full gap-4",{
                "grid-cols-1": groupsWithElements.length < 2,
                "grid-cols-2": groupsWithElements.length >= 2,
                }
            )}>
                {/* Ungrouped items */}
                {responseData.options && responseData.options.length > 0 && (
                    <div className="w-full">
                        <h3 className="mb-2 font-medium">Ungrouped</h3>
                        <div className="flex flex-col gap-1">
                            {responseData.options.map((option: string, index: number) => (
                                <BorderBox key={`ungrouped-${option}-${index}`} className="bg-lighter-gray" size='sm'>
                                    {index + 1}. {option}
                                </BorderBox>
                            ))}
                        </div>
                    </div>
                )}

                {/* Grouped items */}
                {groupsWithElements?.map((groupName: string) => (
                    <div key={groupName} className="flex-1">
                        <h3 className="mb-2 font-medium">{groupName}</h3>
                        <div className="flex flex-col gap-1">
                            {responseData.groupedOptions?.[groupName]?.map((option: string, index: number) => (
                                <BorderBox key={`${groupName}-${option}-${index}`} className="bg-lighter-gray" size='sm'>
                                    {index + 1}. {option}
                                </BorderBox>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    // Render interactive state
    return (
        <div className="">
            <DragDropContext onDragEnd={onDragEnd}>
                <div className="flex gap-4">
                    {/* Ungrouped Column */}
                    <div className="flex-1 min-w-0">
                        <h3 className="mb-2 font-medium text-center text-gray-600">
                            Ungrouped Options
                        </h3>
                        <Droppable droppableId="ungrouped">
                            {(droppableProvided, snapshot) => (
                                <div
                                    className={cn('flex flex-col gap-1 rounded-lg p-1 min-h-[100px] transition-all border-2 border-dashed', {
                                        "bg-green-50 border-green-300": snapshot.isDraggingOver,
                                        "bg-white border-gray-200": !snapshot.isDraggingOver
                                    })}
                                    ref={droppableProvided.innerRef}
                                    {...droppableProvided.droppableProps}
                                >
                                    {groupsAndOptions.options.map((option, index) => (
                                        <Draggable
                                            key={`ungrouped-${option}-${index}`}
                                            draggableId={`ungrouped-${option}-${index}`}
                                            index={index}
                                        >
                                            {(provided, snapshot) => {
                                                const usePortal = snapshot.isDragging;

                                                const child = (
                                                    <BorderBox
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className={cn(
                                                            'flex items-start cursor-grab active:cursor-grabbing',
                                                            {
                                                                'opacity-80 shadow-lg': snapshot.isDragging,
                                                                'hover:shadow-sm': !snapshot.isDragging
                                                            }
                                                        )}
                                                        size='sm'
                                                        style={provided.draggableProps.style}
                                                    >
                                                        <div className="mr-2 font-medium">
                                                            {index + 1}.
                                                        </div>
                                                        <div className="flex-1">
                                                            {option}
                                                        </div>
                                                    </BorderBox>
                                                );

                                                if (!usePortal) {
                                                    return child;
                                                }

                                                return createPortal(child, document.body);
                                            }}
                                        </Draggable>
                                    ))}
                                    {droppableProvided.placeholder}
                                    {groupsAndOptions.options.length === 0 && (
                                        <div className="py-4 text-sm text-center text-gray-400">
                                            All options have been grouped
                                        </div>
                                    )}
                                </div>
                            )}
                        </Droppable>
                    </div>

                    {/* Group Columns */}
                    {groupsAndOptions.groups.map((groupName) => (
                        <div key={groupName} className="flex-1 min-w-0">
                            <h3 className="mb-2 font-medium text-center">{groupName}</h3>
                            <Droppable droppableId={groupName}>
                                {(droppableProvided, snapshot) => (
                                    <div
                                        className={cn(
                                            'flex flex-col gap-1 rounded-lg p-1 min-h-[100px] transition-all border-2 border-dashed',
                                            {
                                                "bg-green-50 border-green-300": snapshot.isDraggingOver,
                                                "bg-white border-gray-200": !snapshot.isDraggingOver
                                            }
                                        )}
                                        ref={droppableProvided.innerRef}
                                        {...droppableProvided.droppableProps}
                                    >
                                        {(groupsAndOptions.groupedOptions?.[groupName] || []).map((option, index) => (
                                            <Draggable
                                                key={`${groupName}-${option}-${index}`}
                                                draggableId={`${groupName}-${option}-${index}`}
                                                index={index}
                                            >
                                                {(provided, snapshot) => {
                                                    const usePortal = snapshot.isDragging;

                                                    const child = (
                                                        <BorderBox
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            className={cn(
                                                                'flex items-start cursor-grab active:cursor-grabbing',
                                                                {
                                                                    'opacity-80 shadow-lg': snapshot.isDragging,
                                                                    'hover:shadow-sm': !snapshot.isDragging
                                                                }
                                                            )}
                                                            size='sm'
                                                            style={provided.draggableProps.style}
                                                        >
                                                            <div className="mr-2 font-medium">
                                                                {index + 1}.
                                                            </div>
                                                            <div className="flex-1">
                                                                {option}
                                                            </div>
                                                        </BorderBox>
                                                    );

                                                    if (!usePortal) {
                                                        return child;
                                                    }

                                                    return createPortal(child, document.body);
                                                }}
                                            </Draggable>
                                        ))}
                                        {droppableProvided.placeholder}
                                        {(!groupsAndOptions.groupedOptions?.[groupName] || groupsAndOptions.groupedOptions[groupName].length === 0) && (
                                            <div className="py-4 text-sm text-center text-gray-400">
                                                Drop items here
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Droppable>
                        </div>
                    ))}
                </div>
            </DragDropContext>
        </div>
    );
}